import { API_URL } from "../../config/connect";

function authHeaders() {
  const token = localStorage.getItem("google_id_token");
  if (!token) throw new Error("Not signed in");
  return {
    Authorization: `Bearer ${token}`,
    "Content-Type": "application/json",
  };
}

async function request(path, { method = "GET", body, query } = {}) {
  let url = `${API_URL}${path}`;
  if (query && Object.keys(query).length) {
    const qs = new URLSearchParams();
    Object.entries(query).forEach(([k, v]) => {
      if (v !== undefined && v !== null && v !== "") qs.append(k, v);
    });
    const s = qs.toString();
    if (s) url += `?${s}`;
  }
  const res = await fetch(url, {
    method,
    headers: authHeaders(),
    body: body ? JSON.stringify(body) : undefined,
  });
  const ct = res.headers.get("content-type") || "";
  const data = ct.includes("application/json") ? await res.json().catch(() => ({})) : await res.text();
  if (!res.ok) {
    const detail = (data && data.error && data.error.message) || (data && data.detail) || res.statusText;
    const err = new Error(typeof detail === "string" ? detail : `HTTP ${res.status}`);
    err.status = res.status;
    err.payload = data;
    throw err;
  }
  return data;
}

/** Multipart жүктеу — `authHeaders()` жарамайды, себебі ол Content-Type-ты
 *  `application/json` деп қояды. FormData-ға шекараны (boundary) браузердің
 *  өзі қоюы керек, сондықтан Content-Type мүлде жіберілмейді. */
async function upload(path, file) {
  const token = localStorage.getItem("google_id_token");
  if (!token) throw new Error("Not signed in");
  const form = new FormData();
  form.append("file", file);
  const res = await fetch(`${API_URL}${path}`, {
    method: "POST",
    headers: { Authorization: `Bearer ${token}` },
    body: form,
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) {
    const err = new Error(data?.detail || `HTTP ${res.status}`);
    err.status = res.status;
    throw err;
  }
  return data;
}

/** Файл/сурет жүктеу → { file_url } */
export const uploadChatFile = (file) =>
  upload("/api/v1/chat/upload/file", file).then((d) => d.file_url);

/** Дауыстық хабарлама жүктеу → URL.
 *  Бекенд `{ audio_url }` қайтарады, бірақ чат кестесінде дауыс `file_url`
 *  бағанында сақталады (мобильді клиенттер де солай жібереді). */
export const uploadChatAudio = (file) =>
  upload("/api/v1/chat/upload/audio", file).then((d) => d.audio_url || d.file_url);

// Queue
export const getQueue = ({ q, has_unread, limit = 100 } = {}) =>
  request("/api/v1/operator/queue", { query: { q, has_unread, limit } });

// Search users (start chat with anyone)
export const searchUsers = (q, limit = 20) =>
  request("/api/v1/operator/users/search", { query: { q, limit } });

export const startChatWith = (targetUserId) =>
  request("/api/v1/operator/chats/start", {
    method: "POST",
    body: { target_user_id: targetUserId },
  });

export const claimChat = (chatId, priority = "normal") =>
  request(`/api/v1/operator/queue/${chatId}/claim`, {
    method: "POST",
    body: { priority },
  });

// My chats
export const getMyChats = ({
  status = "open",
  q,
  priority,
  has_unread,
  page = 1,
  limit = 50,
} = {}) =>
  request("/api/v1/operator/my-chats", {
    query: { status, q, priority, has_unread, page, limit },
  });

// Messages
export const getMessages = (chatId, { before, limit = 50 } = {}) =>
  request(`/api/v1/operator/chats/${chatId}/messages`, { query: { before, limit } });

// Close / reopen / transfer
export const closeChat = (chatId, resolutionNote, tags = []) =>
  request(`/api/v1/operator/chats/${chatId}/close`, {
    method: "POST",
    body: { resolution_note: resolutionNote, tags },
  });

export const reopenChat = (chatId) =>
  request(`/api/v1/operator/chats/${chatId}/reopen`, { method: "POST" });

export const transferChat = (chatId, targetOperatorId, note) =>
  request(`/api/v1/operator/chats/${chatId}/transfer`, {
    method: "POST",
    body: { target_operator_id: targetOperatorId, note },
  });

// Internal notes
export const listInternalNotes = (chatId) =>
  request(`/api/v1/operator/chats/${chatId}/internal-notes`);

export const addInternalNote = (chatId, body) =>
  request(`/api/v1/operator/chats/${chatId}/internal-notes`, {
    method: "POST",
    body: { body },
  });

export const deleteInternalNote = (chatId, noteId) =>
  request(`/api/v1/operator/chats/${chatId}/internal-notes/${noteId}`, {
    method: "DELETE",
  });

// Canned responses
export const listCanned = (language) =>
  request("/api/v1/operator/canned-responses", { query: { language } });

export const createCanned = (data) =>
  request("/api/v1/operator/canned-responses", { method: "POST", body: data });

export const updateCanned = (id, data) =>
  request(`/api/v1/operator/canned-responses/${id}`, { method: "PATCH", body: data });

export const deleteCanned = (id) =>
  request(`/api/v1/operator/canned-responses/${id}`, { method: "DELETE" });

// User 360
export const getUserProfile = (chatId) =>
  request(`/api/v1/operator/chats/${chatId}/user-profile`);

// Нақты онлайн (сокет) + соңғы рет қашан: { users: { "<id>": { is_online, last_online } } }
export const getPresence = (userIds) =>
  request("/api/v1/operator/presence", { query: { user_ids: [].concat(userIds).join(",") } });

// Stats
export const getMyStats = () => request("/api/v1/operator/stats/me");
